/**
 * The languages the platform speaks. Adding one is an entry here plus its catalogue files
 * (`npm run i18n:extract` creates them); nothing else in the apps names a language.
 *
 * `intl` is the tag handed to `Intl.*`. Arabic pins the Gregorian calendar and Latin digits, so that dates, order
 * numbers and prices read the same as on invoices and in Business Central; `ar-EG` gives the Egyptian month names.
 */
export const locales = {
  en: { name: "English", dir: "ltr", intl: "en-GB" },
  ar: { name: "العربية", dir: "rtl", intl: "ar-EG-u-ca-gregory-nu-latn" },
  /** Development only: every extracted string accented and the layout right to left, so that untranslated text and
   *  left/right mistakes show on any page before a real translation exists. */
  pseudo: { name: "Pseudo", dir: "rtl", intl: "en-GB" },
} as const satisfies Record<string, { name: string; dir: "ltr" | "rtl"; intl: string }>;

export type Locale = keyof typeof locales;

const cookie = "ghz.locale";
let active: Locale = "en";
let pseudoAllowed = false;
let offered: readonly Locale[] = ["en", "ar"];

function isLocale(value: string | null | undefined): value is Locale {
  return !!value && Object.hasOwn(locales, value) && (value === "pseudo" ? pseudoAllowed : offered.includes(value as Locale));
}

function readCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${cookie.replace(".", "\\.")}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

/**
 * The site every app shares (`.ghezaa-me.com` for core., orders., support.), so a language chosen in one app opens the
 * next page of any other in it. None on localhost or an address: those share cookies across ports already.
 */
function sharedDomain(): string | null {
  const host = window.location.hostname;
  if (host === "localhost" || host.endsWith(".localhost") || /^[\d.]+$/.test(host) || host.includes(":")) return null;
  const labels = host.split(".");
  return labels.length > 2 ? `.${labels.slice(-2).join(".")}` : null;
}

function writeCookie(locale: Locale) {
  const domain = sharedDomain();
  if (domain) document.cookie = `${cookie}=; path=/; max-age=0`; // the older per-app copy, so only the shared one is read
  document.cookie = `${cookie}=${locale}; path=/; max-age=31536000; samesite=lax${domain ? `; domain=${domain}` : ""}`;
}

/**
 * Settles the language for this page load, before anything renders: `?lang=` (remembered), then the remembered
 * choice, then English. Sets `<html lang dir>`, which is all the layout needs to turn right to left.
 * `languages` are the ones this page is translated into (an English-only back office passes `["en"]`: a remembered
 * Arabic then shows English there, and stays remembered for the pages that have it). Pass `pseudo:
 * import.meta.env.DEV`; the pseudo-locale is refused otherwise.
 */
export function initLocale({ pseudo = false, languages = ["en", "ar"] }: { pseudo?: boolean; languages?: readonly Locale[] } = {}): Locale {
  pseudoAllowed = pseudo;
  offered = languages;
  const url = new URL(window.location.href);
  const requested = url.searchParams.get("lang");
  if (isLocale(requested)) {
    writeCookie(requested);
    // A link that names a language is a choice, like the switch: a profile adopts it.
    if (requested !== "pseudo") notePicked(requested);
  }
  if (requested !== null) {
    url.searchParams.delete("lang");
    window.history.replaceState(window.history.state, "", url);
  }
  const remembered = readCookie();
  active = isLocale(requested) ? requested : isLocale(remembered) ? remembered : "en";
  document.documentElement.lang = active === "pseudo" ? "en" : active;
  document.documentElement.dir = locales[active].dir;
  return active;
}

/** Remembers a language and reloads, so every formatter and catalogue starts in it. */
export function chooseLocale(locale: Locale) {
  writeCookie(locale);
  window.location.reload();
}

const picked = "ghz.locale.picked";

/** Notes that the person picked this language themselves (the switch), so a profile can adopt it once they sign in. */
export function notePicked(locale: Locale) {
  try {
    sessionStorage.setItem(picked, locale);
  } catch {
    // Storage refused: the choice still holds in the cookie.
  }
}

/** The language the person picked with the switch in this browser session, read once; null when they did not. */
export function takePickedLocale(): Locale | null {
  try {
    const value = sessionStorage.getItem(picked);
    sessionStorage.removeItem(picked);
    return value && Object.hasOwn(locales, value) && value !== "pseudo" ? (value as Locale) : null;
  } catch {
    return null;
  }
}

export function currentLocale(): Locale {
  return active;
}

/** The languages this page offers (pseudo excluded), for a switcher. */
export function offeredLocales(): readonly Locale[] {
  return offered;
}

/** The language this browser remembers having chosen, whatever this page offers; null when none was ever chosen. */
export function rememberedLocale(): Locale | null {
  const value = readCookie();
  return value && Object.hasOwn(locales, value) && value !== "pseudo" ? (value as Locale) : null;
}

/** The tag for `Intl.*` and `toLocaleString` in the current language. */
export function intlLocale(): string {
  return locales[active].intl;
}

/** `1,234` in the current language. */
export function formatCount(n: number): string {
  return n.toLocaleString(intlLocale());
}
